import { useEffect, useState } from "react";
import { HardDrive, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Rough browser localStorage cap (Chrome/Firefox ~5 MB per origin).
const APPROX_QUOTA_BYTES = 5 * 1024 * 1024;

function measureLocalStorage() {
  try {
    let bytes = 0;
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const v = localStorage.getItem(k) || "";
      const sz = (k.length + v.length) * 2; // UTF-16
      bytes += sz;
      entries.push({ key: k, size: sz });
    }
    entries.sort((a, b) => b.size - a.size);
    return { bytes, entries };
  } catch {
    return { bytes: 0, entries: [] };
  }
}

const fmtBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function StorageHealthCard() {
  const [{ bytes, entries }, setState] = useState({ bytes: 0, entries: [] });

  const refresh = () => setState(measureLocalStorage());
  useEffect(() => { refresh(); }, []);

  const pct = Math.min(100, Math.round((bytes / APPROX_QUOTA_BYTES) * 1000) / 10);
  const barColor = pct >= 80 ? "bg-red-500" : pct >= 50 ? "bg-amber-500" : "bg-emerald-500";
  const ringColor = pct >= 80 ? "text-red-400" : pct >= 50 ? "text-amber-400" : "text-emerald-400";

  const clearCache = () => {
    // Keep the JWT so the admin stays logged in.
    const token = localStorage.getItem("shakthi_token");
    try {
      localStorage.clear();
      if (token) localStorage.setItem("shakthi_token", token);
    } catch { /* ignore */ }
    // Also drop any sessionStorage the app may accumulate.
    try { sessionStorage.clear(); } catch { /* ignore */ }
    toast.success("Browser cache cleared · session kept");
    refresh();
  };

  return (
    <div className="card-surface p-6" data-testid="storage-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <HardDrive className="w-4 h-4 text-amber-400"/>
          <div className="font-heading text-white text-sm">Browser Storage Health</div>
        </div>
        <button
          onClick={refresh}
          data-testid="storage-refresh"
          className="w-7 h-7 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 flex items-center justify-center text-slate-400">
          <RefreshCw className="w-3.5 h-3.5"/>
        </button>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <div className={`font-display text-3xl ${ringColor}`} data-testid="storage-used">{fmtBytes(bytes)}</div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">/ ~{fmtBytes(APPROX_QUOTA_BYTES)} quota</div>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden mb-3">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.max(pct, 2)}%` }} data-testid="storage-bar"/>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        {pct >= 80
          ? "⚠️ Nearing quota — clear the cache to avoid login/quota failures."
          : pct >= 50
          ? "Comfortable. Clear the cache if the app starts refusing writes."
          : "Healthy. Nothing to do."}
      </div>
      {entries.length > 0 && (
        <div className="rounded-md border border-slate-800 bg-slate-950/60 p-2 mb-3 space-y-1 max-h-32 overflow-y-auto">
          {entries.slice(0, 5).map((e) => (
            <div key={e.key} className="flex items-center justify-between text-[11px] font-mono">
              <span className="text-slate-400 truncate mr-2">{e.key}</span>
              <span className="text-slate-300">{fmtBytes(e.size)}</span>
            </div>
          ))}
        </div>
      )}
      <Button
        onClick={clearCache}
        data-testid="storage-clear-btn"
        className="w-full bg-red-500/90 hover:bg-red-500 text-white font-bold rounded-full text-xs">
        <Trash2 className="w-3.5 h-3.5 mr-1.5"/> Clear Cache (keeps you logged in)
      </Button>
    </div>
  );
}
